import pluralize from 'pluralize'
import { useCallback } from 'react'
import { toast } from 'react-toastify'

import { interpretCriteriaWithRetry } from 'features/trend-detection/features/agent-create/utils/agent-filter-criteria'
import { useInterpretFilters } from 'features/trend-detection/hooks/use-interpret-filters'
import type { TrendDetectionService } from 'features/trend-detection/service/service'
import type { FilterCriterion } from 'features/trend-detection/types'

export interface UseAgentFilterSuggestionsParams {
  service: TrendDetectionService
  projectId: string | null
  prompt: string
  agentName: string
  criteria: FilterCriterion[]
  setCriteria: (criteria: FilterCriterion[]) => void
  /** Receives the names (uuid → theme name) the interpret call emitted. */
  setSuggestedLabels: (labels: Record<string, string>) => void
}

export interface UseAgentFilterSuggestionsResult {
  generatingFilters: boolean
  /** The explicit "Suggest filters" button behind Issue Agent's Instructions
   * textarea. */
  suggestFilters: () => Promise<void>
  /** Fires autogenerate iff `criteria` is still empty at submit time — the
   * implicit trigger the deleted review step's `prepareForReview` gave, at
   * the last honest moment now that there's no intermediate step to run it
   * in the background of. Returns the criteria to submit with (the caller's
   * own `criteria` state won't have re-rendered yet when this resolves). */
  ensureFiltersBeforeSubmit: () => Promise<FilterCriterion[]>
}

/** Private sub-hook of `useAgentCreateForm` — the LLM filter-suggestion
 * flow, split out so the orchestrator stays under the hook-max-lines
 * threshold. Not re-exported from the directory's `index.ts`. */
export function useAgentFilterSuggestions({
  service,
  projectId,
  prompt,
  agentName,
  criteria,
  setCriteria,
  setSuggestedLabels,
}: UseAgentFilterSuggestionsParams): UseAgentFilterSuggestionsResult {
  const { runInterpret, isInterpreting: generatingFilters } =
    useInterpretFilters({ service, projectId })

  const suggestFilters = useCallback(async (): Promise<void> => {
    try {
      const { criteria: result, valueLabels } =
        await interpretCriteriaWithRetry(
          runInterpret,
          prompt.trim() || agentName.trim(),
        )
      setCriteria(result)
      setSuggestedLabels(valueLabels)
      if (result.length > 0) {
        toast.success(`Added ${pluralize('filter', result.length, true)}`)
      } else {
        toast.info(
          'No filters matched that description. Try adding them manually.',
        )
      }
    } catch {
      toast.error(
        "Couldn't generate filters. Try again or add filters manually.",
      )
    }
  }, [prompt, agentName, runInterpret, setCriteria, setSuggestedLabels])

  const ensureFiltersBeforeSubmit = useCallback(async (): Promise<
    FilterCriterion[]
  > => {
    if (criteria.length > 0) return criteria
    try {
      const { criteria: result, valueLabels } =
        await interpretCriteriaWithRetry(
          runInterpret,
          prompt.trim() || agentName.trim(),
        )
      setCriteria(result)
      setSuggestedLabels(valueLabels)
      return result
    } catch {
      // Submit proceeds with no filters rather than blocking creation on a
      // best-effort suggestion that failed.
      return criteria
    }
  }, [
    criteria,
    prompt,
    agentName,
    runInterpret,
    setCriteria,
    setSuggestedLabels,
  ])

  return { generatingFilters, suggestFilters, ensureFiltersBeforeSubmit }
}
